import { FC, Fragment, useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import {
    Grid,
    DialogActions,
    DialogContent,
    TextField,
    CircularProgress,
    Button,
    MenuItem,
    Box
} from "@mui/material";
import { useSnackbar } from "notistack";
import http from "src/utils/httpHelper";
import { ROUTES } from "src/utils/httpHelper";
import anyToString from "src/utils/anyToString";
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import moment from "moment";

interface ModulesFormProps {
    modalCloseCallback,
    onSuccessCallback
    data?: any
}

const ModulesForm: FC<ModulesFormProps> = ({ modalCloseCallback, onSuccessCallback, data }) => {
    const { t }: { t: any } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [municipalities, setMunicipalities] = useState([]);

    const iniVal = {
        name: data ? data.name : '',
        municipality_id: data ? data.municipality_id : '',
        active: data ? (data.active == 1 ? true : false) : true,
        closing_time: data ? data.closing_time : '15:00',
        opening_time: data ? data.opening_time : '08:00',
        submit: null
    }

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .max(191)
            .required(t("VALIDATION.REQUIRED")),
        municipality_id: Yup.number()
            .required(t("VALIDATION.REQUIRED")),
        opening_time: Yup.string()
            .required(t("VALIDATION.REQUIRED"))
            .test('MAX_OPENING_TIME', t("VALIDATION.MAX_OPENING_TIME"), function (value, extra) { return (value < extra.parent.closing_time) }),
        closing_time: Yup.string()
            .required(t("VALIDATION.REQUIRED"))
            .test('MIN_CLOSING_TIME', t("VALIDATION.MIN_CLOSING_TIME"), function (value, extra) { return (value > extra.parent.opening_time) }),
    })


    useEffect(() => {
        http.get(ROUTES.MUNICIPALITIES_FOR_SELECT)
            .then((municipalities: any) => {
                setMunicipalities(municipalities);
            })
            .catch((error) => {
                enqueueSnackbar(error.message, { variant: 'error' });
            });
    }, []);

    return (
        <>
            <Formik
                initialValues={iniVal}

                validationSchema={validationSchema}

                onSubmit={async (
                    _values,
                    { resetForm, setErrors, setStatus, setSubmitting }
                ) => {
                    if (data) {
                        await http.post(ROUTES.MODULES_RESOURCE + '/' + data.id, { ..._values, _method: 'put' })
                            .then((response: any) => {
                                setStatus({ success: true });
                                resetForm();
                                onSuccessCallback();
                                enqueueSnackbar(response.message, { variant: 'success' });
                            })
                            .catch((error) => {
                                console.error(error);
                                enqueueSnackbar(error.message, { variant: 'error' });
                                setStatus({ success: false });
                                setErrors({ submit: error.message });
                            })
                            .finally(() => {
                                setSubmitting(false);
                            });
                    } else {
                        await http.post(ROUTES.MODULES_RESOURCE, _values)
                            .then((response: any) => {
                                setStatus({ success: true });
                                resetForm();
                                onSuccessCallback();
                                enqueueSnackbar(response.message, { variant: 'success' });
                            })
                            .catch((error) => {
                                console.error(error);
                                enqueueSnackbar(error.message, { variant: 'error' });
                                setStatus({ success: false });
                                setErrors({ submit: error.message });
                            })
                            .finally(() => {
                                setSubmitting(false);
                            });
                    }
                }}
            >
                {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    touched,
                    values,
                    resetForm
                }) => (
                    <form onSubmit={handleSubmit}>
                        <DialogContent
                            dividers
                            sx={{
                                p: 3,
                            }}
                        >
                            <Grid container spacing={3}>
                                <Grid item xs={8} md={10}>
                                    <TextField
                                        error={Boolean(touched.name && errors.name)}
                                        fullWidth
                                        helperText={touched.name && errors.name && anyToString(errors.name)}
                                        label={t("FIELDS.NAME")}
                                        name="name"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.name}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={4} md={2}>
                                    <FormControlLabel
                                        value="top"
                                        control={<Switch color="success" onChange={handleChange} name="active" checked={values.active} />}
                                        label={t("FIELDS.ACTIVE")}
                                        labelPlacement="top"
                                    />

                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <TextField
                                        fullWidth={true}
                                        select
                                        label={t('FIELDS.MUNICIPALITY')}
                                        name="municipality_id"
                                        value={values.municipality_id}
                                        onChange={handleChange}
                                        error={Boolean(touched.municipality_id && errors.municipality_id)}
                                        helperText={touched.municipality_id && errors.municipality_id && anyToString(errors.municipality_id)}
                                    >
                                        {municipalities.map((muncipality) => (
                                            <MenuItem key={muncipality.id} value={muncipality.id}>
                                                {muncipality.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth={true}
                                        label={t('FIELDS.OPENING_TIME')}
                                        type="time"
                                        name="opening_time"
                                        value={values.opening_time}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={Boolean(touched.opening_time && errors.opening_time)}
                                        helperText={touched.opening_time && errors.opening_time && anyToString(errors.opening_time)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth={true}
                                        label={t('FIELDS.CLOSING_TIME')}
                                        type="time"
                                        name="closing_time"
                                        value={values.closing_time}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        error={Boolean(touched.closing_time && errors.closing_time)}
                                        helperText={touched.closing_time && errors.closing_time && anyToString(errors.closing_time)}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions
                            sx={{
                                p: 3,
                                display: 'flex',
                                justifyContent: !data ? 'space-between' : 'flex-end'
                            }}
                        >
                            {
                                !data &&
                                <Button color="warning" onClick={() => resetForm()}>
                                    {t("GENERAL.RESET")}
                                </Button>
                            }
                            <Box>
                                <Button color="secondary" onClick={modalCloseCallback}>
                                    {t("GENERAL.CANCEL")}
                                </Button>
                                <Button
                                    sx={{
                                        ml: 2
                                    }}
                                    type="submit"
                                    startIcon={
                                        isSubmitting ? <CircularProgress size="1rem" /> : null
                                    }
                                    disabled={Boolean(errors.submit) || isSubmitting}
                                    variant="contained"
                                >
                                    {t(!data ? "MODULES.CREATE" : 'MODULES.EDIT')}
                                </Button>
                            </Box>
                        </DialogActions>
                    </form>
                )}
            </Formik>
        </>
    )
}

export default ModulesForm;